import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  CustomFieldRenderProps,
  F0Dialog,
  F0Form,
  F0Select,
  F0SelectItemProps,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  AtsContractTypesEnum,
  AtsCreateForHiringPlanRequisitionDocument,
  AtsJobPostingSalaryFormatEnum,
  AtsJobPostingSalaryPeriodEnum,
  AtsJobRequisitionsListDocument,
  AtsNewVacancyRequisitionModalDocument,
  AtsRequisitionReasonsEnum,
  AtsVacancyRequisitionContractTypeFragment,
  AtsWorkplaceTypesEnum,
  OnboardingEntitiesWorkScheduleTimeEnum,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useSingleTeamSelectorFormField } from 'modules/core/lib/formFields/teamSelector'

import { JobCatalogRoleSelector } from 'modules/ats/components/job_catalog/JobCatalogRoleSelector'
import { useSalaryConditionsAutoFill } from 'modules/ats/lib/utils/useSalaryConditionsAutoFill'

import { NewVacancyRequisitionModalSkeleton } from './Skeleton'

const WORKPLACE_TYPES = ['onsite', 'hybrid', 'remote'] as const
const SCHEDULE_TYPES = ['full_time', 'part_time'] as const
const VACANCY_TYPES = ['new_position', 'backfill'] as const
const SALARY_PERIODS = ['annual', 'monthly', 'daily'] as const
const SALARY_FORMATS = ['range', 'fixed_amount'] as const

interface FormValues {
  name: string
  quantity: number | undefined
  jobCatalog: {
    roleUuid: string | null
    levelUuid: string | null
  }
  locationId: string
  teamId: string
  workplaceType: AtsWorkplaceTypesEnum | undefined
  vacancyType: AtsRequisitionReasonsEnum | undefined
  contractType: AtsContractTypesEnum | undefined
  scheduleType: OnboardingEntitiesWorkScheduleTimeEnum | undefined
  deadline: Date | undefined
  salaryPeriod: AtsJobPostingSalaryPeriodEnum | undefined
  salaryFormat: AtsJobPostingSalaryFormatEnum | undefined
  salaryCurrency: string | undefined
  salaryFromAmount: number | undefined
  salaryToAmount: number | undefined
  notes: string
}

const NewVacancyRequisitionModalPage = () => {
  const { company, access, employee } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data } = useQuery(AtsNewVacancyRequisitionModalDocument)

  const [createRequisition] = useMutation(AtsCreateForHiringPlanRequisitionDocument, {
    extractorFn: (mutationData) => mutationData.atsMutations.createForHiringPlanAtsRequisition,
    refetchQueries: [AtsJobRequisitionsListDocument],
  })

  const [levelUuid, setLevelUuid] = useState<string | null>(null)
  const [selectedLocationId, setSelectedLocationId] = useState<string>('')

  const jobCatalogRoles = useMemo(
    () => data?.jobCatalog.treeNodesConnection.nodes ?? [],
    [data?.jobCatalog.treeNodesConnection.nodes]
  )

  const contractTypes = useMemo(
    () =>
      (data?.ats.contractTypesConnection.nodes ?? []).filter(
        (type): type is AtsVacancyRequisitionContractTypeFragment => Boolean(type?.label)
      ),
    [data?.ats.contractTypesConnection.nodes]
  )

  const contractTypeSelectOptions = useMemo(() => {
    const grouped = groupBy(contractTypes, (item) => item.country ?? 'all')
    const options: F0SelectItemProps<string>[] = []

    Object.entries(grouped).forEach(([countryKey, items], index) => {
      if (index > 0) {
        options.push({ type: 'separator' })
      }
      options.push({
        value: `header_${countryKey}`,
        label: countryKey,
        disabled: true,
      })
      items
        .filter((i) => i.label)
        .sort((x, y) => (x.label ?? '').localeCompare(y.label ?? ''))
        .forEach(({ id, label }) => {
          options.push({ value: id, label: label ?? '' })
        })
    })

    return options
  }, [contractTypes])

  const currencies = useMemo(
    () =>
      [...(data?.apiCore?.currenciesConnection?.edges ?? [])]
        .map((c) => ({ label: c.node.isoCode, value: c.node.isoCode }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [data?.apiCore?.currenciesConnection?.edges]
  )

  const locationOptions = useMemo(
    () =>
      (data?.locations?.locations ?? [])
        .map((loc) => ({ label: loc.name, value: loc.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data?.locations?.locations]
  )

  const defaultCurrency = useMemo(() => {
    const companyCurrency = company?.currency
    const fromCompany =
      companyCurrency && currencies.some((opt) => opt.value === companyCurrency)
        ? companyCurrency
        : null
    const fromPriority = data?.apiCore?.currenciesConnection?.edges?.find(
      (c) => c.node.priority === 1
    )?.node.isoCode
    return fromCompany ?? fromPriority ?? currencies[0]?.value ?? 'EUR'
  }, [company?.currency, currencies, data?.apiCore?.currenciesConnection?.edges])

  const teamField = useSingleTeamSelectorFormField({
    label: i18n.t('ats.vacancy_requisition_form.team_label'),
    placeholder: i18n.t('ats.vacancy_requisition_form.team_placeholder'),
    mandatory: true,
    row: 'workplace-team',
  })

  const schema = useMemo(
    () =>
      z.object({
        name: f0FormField(z.string().min(1, i18n.t('common.validations.empty')), {
          label: i18n.t('ats.vacancy_requisition_form.name_label'),
          placeholder: i18n.t('ats.vacancy_requisition_form.name_placeholder'),
          row: 'name-quantity',
        }),
        quantity: f0FormField(z.number({ required_error: i18n.t('common.validations.empty') }), {
          label: i18n.t('ats.vacancy_requisition_form.quantity_label'),
          placeholder: i18n.t('ats.vacancy_requisition_form.quantity_placeholder'),
          fieldType: 'number',
          row: 'name-quantity',
        }),
        jobCatalog: f0FormField(
          z.object({
            roleUuid: z.string().nullable(),
            levelUuid: z.string().nullable(),
          }),
          {
            fieldType: 'custom',
            label: '',
            renderIf: () => jobCatalogRoles.length > 0,
            render: (props: CustomFieldRenderProps<FormValues['jobCatalog']>) => (
              <JobCatalogRoleSelector
                onLevelSelect={(roleUuid, roleLevelUuid) => {
                  props.onChange({
                    roleUuid: roleUuid ?? null,
                    levelUuid: roleLevelUuid ?? null,
                  })
                  setLevelUuid(roleLevelUuid ?? null)
                }}
                jobCatalogRoles={jobCatalogRoles}
              />
            ),
          }
        ),
        locationId: f0FormField(z.string().min(1, i18n.t('common.validations.empty')), {
          label: i18n.t('ats.vacancy_requisition_form.workplace_label'),
          fieldType: 'custom',
          row: 'workplace-team',
          render: (props: CustomFieldRenderProps<string>) => (
            <F0Select
              label={i18n.t('ats.vacancy_requisition_form.workplace_label')}
              name="locationId"
              placeholder={i18n.t('ats.vacancy_requisition_form.workplace_placeholder')}
              value={props.value || undefined}
              onChange={(value: string) => {
                props.onChange(value)
                setSelectedLocationId(value)
              }}
              options={locationOptions}
              showSearchBox
              size="md"
              error={props.error}
              mandatory
            />
          ),
        }),
        teamId: teamField,
        workplaceType: f0FormField(
          z.enum(WORKPLACE_TYPES, {
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.vacancy_requisition_form.type_of_location_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.type_of_location_placeholder'),
            fieldType: 'select',
            row: 'location-vacancy-type',
            options: [
              { label: i18n.t('ats.workplace_types.onsite'), value: 'onsite' },
              { label: i18n.t('ats.workplace_types.hybrid'), value: 'hybrid' },
              { label: i18n.t('ats.workplace_types.remote'), value: 'remote' },
            ],
          }
        ),
        vacancyType: f0FormField(
          z.enum(VACANCY_TYPES, {
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.vacancy_requisition_form.vacancy_type_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.vacancy_type_placeholder'),
            fieldType: 'select',
            row: 'location-vacancy-type',
            options: [
              {
                label: i18n.t('ats.requisitions_form.reason.options.new_position'),
                value: 'new_position',
              },
              {
                label: i18n.t('ats.requisitions_form.reason.options.backfill'),
                value: 'backfill',
              },
            ],
          }
        ),
        contractType: f0FormField(
          z.custom<AtsContractTypesEnum>(
            (val) => typeof val === 'string' && contractTypes.some((ct) => ct.id === val),
            { message: i18n.t('common.validations.empty') }
          ),
          {
            label: i18n.t('ats.vacancy_requisition_form.agreement_type_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.agreement_type_placeholder'),
            fieldType: 'select',
            row: 'agreement-schedule',
            options: contractTypeSelectOptions,
          }
        ),
        scheduleType: f0FormField(
          z.enum(SCHEDULE_TYPES, {
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.vacancy_requisition_form.schedule_type_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.schedule_type_placeholder'),
            fieldType: 'select',
            row: 'agreement-schedule',
            options: [
              { label: i18n.t('ats.job_schedule.full_time'), value: 'full_time' },
              { label: i18n.t('ats.job_schedule.part_time'), value: 'part_time' },
            ],
          }
        ),
        deadline: f0FormField(
          z.date({
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.vacancy_requisition_form.deadline_label'),
            placeholder: i18n.t('common.select'),
          }
        ),
        salaryPeriod: f0FormField(
          z.enum(SALARY_PERIODS, {
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.vacancy_requisition_form.salary_period_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.salary_period_placeholder'),
            fieldType: 'select',
            row: 'salary-period-type-currency',
            options: [
              { label: i18n.t('ats.job_salary_period.annual'), value: 'annual' },
              { label: i18n.t('ats.job_salary_period.monthly'), value: 'monthly' },
              { label: i18n.t('ats.job_salary_period.daily'), value: 'daily' },
            ],
          }
        ),
        salaryFormat: f0FormField(
          z.enum(SALARY_FORMATS, {
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.vacancy_requisition_form.salary_type_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.salary_type_placeholder'),
            fieldType: 'select',
            row: 'salary-period-type-currency',
            options: [
              { label: i18n.t('ats.job_salary_format.range'), value: 'range' },
              { label: i18n.t('ats.job_salary_format.fixed_amount'), value: 'fixed_amount' },
            ],
          }
        ),
        salaryCurrency: f0FormField(
          z
            .string({ required_error: i18n.t('common.validations.empty') })
            .min(1, i18n.t('common.validations.empty')),
          {
            label: i18n.t('ats.vacancy_requisition_form.currency_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.currency_placeholder'),
            fieldType: 'select',
            row: 'salary-period-type-currency',
            options: currencies,
          }
        ),
        salaryFromAmount: f0FormField(
          z.number({
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.vacancy_requisition_form.amount_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.amount_placeholder'),
            fieldType: 'number',
            row: 'salary-amounts',
          }
        ),
        salaryToAmount: f0FormField(
          z.number({ required_error: i18n.t('common.validations.empty') }),
          {
            label: i18n.t('ats.vacancy_requisition_form.amount_to_label'),
            placeholder: i18n.t('ats.vacancy_requisition_form.amount_placeholder'),
            fieldType: 'number',
            row: 'salary-amounts',
            renderIf: ({ values }) => {
              return values.salaryFormat === 'range'
            },
          }
        ),
        notes: f0FormField(z.string().optional().default(''), {
          label: i18n.t('ats.vacancy_requisition_form.notes_label'),
          fieldType: 'textarea',
        }),
      }),
    [
      jobCatalogRoles,
      locationOptions,
      teamField,
      contractTypes,
      contractTypeSelectOptions,
      currencies,
    ]
  )

  const formDefinition = useF0FormDefinition({
    name: 'new-vacancy-requisition',
    schema,
    defaultValues: {
      name: '',
      quantity: undefined,
      jobCatalog: {
        roleUuid: null,
        levelUuid: null,
      },
      locationId: '',
      teamId: '',
      workplaceType: undefined,
      vacancyType: undefined,
      contractType: undefined,
      scheduleType: undefined,
      deadline: undefined,
      salaryPeriod: undefined,
      salaryFormat: undefined,
      salaryCurrency: defaultCurrency,
      salaryFromAmount: undefined,
      salaryToAmount: undefined,
      notes: '',
    },
    submitConfig: { type: 'default', hideSubmitButton: true, hideActionBar: true },
    errorTriggerMode: 'on-submit',
    onSubmit: async ({ data: values }) => {
      if (
        !values.workplaceType ||
        !values.vacancyType ||
        !values.salaryFormat ||
        !values.salaryCurrency ||
        !values.deadline ||
        !values.teamId
      ) {
        return { success: false }
      }

      let fieldErrors: Record<string, string> = {}

      const convertToCents = (value: number | undefined) =>
        value !== undefined && isFinite(value) ? Math.round(value * 100) : 0

      const result = await createRequisition({
        variables: {
          authorId: access.id,
          companyId: company.id,
          jobPostingTitle: values.name,
          numberOfPositions: values.quantity,
          jobCatalogRoleUuid: values.jobCatalog.roleUuid,
          jobCatalogLevelUuid: values.jobCatalog.levelUuid,
          workplaceId: values.locationId,
          teamId: values.teamId,
          workplaceType: values.workplaceType,
          reason: values.vacancyType,
          contractType: values.contractType ?? null,
          scheduleType: values.scheduleType ?? null,
          deadline: i18nDateToString(values.deadline, 'backend'),
          salaryPeriod: values.salaryPeriod ?? null,
          salaryType: values.salaryFormat,
          salaryCurrency: values.salaryCurrency,
          salaryFromAmountInCents: convertToCents(values.salaryFromAmount),
          salaryToAmountInCents:
            values.salaryToAmount != null ? convertToCents(values.salaryToAmount) : null,
          note: values.notes || null,
          lineManagerId: employee.id,
        },
      })

      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.vacancy_requisition_form.success'),
        })
        navigate(navigation.ats.hiringPlan.requisitions.fullPath())
        return { success: true }
      }

      return { success: false, errors: fieldErrors }
    },
  })

  const { formRef, submit, isSubmitting, setValue } = useF0Form()

  useSalaryConditionsAutoFill({
    jobCatalogLevelUuid: levelUuid,
    workplaceId: selectedLocationId,
    onSalaryConditionsLoaded: (salaryValues) => {
      setValue('salaryFormat', salaryValues.salaryFormat)
      if (salaryValues.salaryPeriod) {
        setValue('salaryPeriod', salaryValues.salaryPeriod)
      }
      setValue('salaryFromAmount', salaryValues.salaryFromAmountInCents)
      setValue('salaryToAmount', salaryValues.salaryToAmountInCents)
    },
  })

  const onClose = () => {
    navigate(navigation.ats.hiringPlan.requisitions.fullPath())
  }

  const loading = !data

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      title={i18n.t('ats.vacancy_requisition_form.title')}
      primaryAction={{
        label: i18n.t('ats.vacancy_requisition_form.create_button'),
        onClick: () => {
          submit().catch(() => {})
        },
        loading: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading ? (
        <NewVacancyRequisitionModalSkeleton />
      ) : (
        <F0Form formDefinition={formDefinition} formRef={formRef} />
      )}
    </F0Dialog>
  )
}

export default NewVacancyRequisitionModalPage
